import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Portal component that renders its children into a new DOM node that exists outside
 * the DOM hierarchy of the parent component. This is useful for modals, tooltips,
 * and other components that need to "break out" of their parent's DOM hierarchy,
 * especially when dealing with z-index and stacking contexts.
 */
interface PortalProps {
  children: React.ReactNode;
}

export const Portal: React.FC<PortalProps> = ({ children }) => {
  // Create a div element that will be the container for our portal
  // Using useState ensures the div is only created once and persists between renders
  const [container] = useState(() => document.createElement('div'));

  useEffect(() => {
    // Append the container div to the body when the Portal is mounted
    document.body.appendChild(container);

    // Cleanup: remove the container div from the body when Portal is unmounted
    // This prevents memory leaks and keeps the DOM clean
    return () => {
      document.body.removeChild(container);
    };
  }, [container]); // Only run effect when container changes (which is never in this case)

  // Use createPortal to render the children into our container div
  // This maintains React's event system and context even though the DOM nodes
  // are in different places in the DOM tree
  return createPortal(children, container);
};